// src/components/dashboards/LabTechnicianDashboard.jsx
// UPDATED: Sample collection moved to SampleCollector.jsx
// This dashboard now handles ANALYSIS & RESULTS only (SAMPLE_COLLECTED + IN_PROGRESS)
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../shared/DashboardLayout';
import { labAPI } from '../../services/api';

// ─── Role → Category mapping (must match backend ROLE_TO_CATEGORY) ────────────
const ROLE_META = {
  LAB_TECH_MICROBIOLOGY: {
    label: 'Microbiology', icon: '🦠', color: '#92400e', bg: '#fef3c7',
    border: '#fcd34d', gradient: 'linear-gradient(135deg,#d97706,#92400e)',
  },
  LAB_TECH_PATHOLOGY: {
    label: 'Pathology', icon: '🔬', color: '#5b21b6', bg: '#ede9fe',
    border: '#c4b5fd', gradient: 'linear-gradient(135deg,#7c3aed,#5b21b6)',
  },
  LAB_TECH_BIOCHEMISTRY: {
    label: 'Biochemistry', icon: '⚗️', color: '#0369a1', bg: '#e0f2fe',
    border: '#7dd3fc', gradient: 'linear-gradient(135deg,#0284c7,#0369a1)',
  },
};

const C = {
  pageBg:'#f0f4f8', surface:'#ffffff',
  green:'#16a34a', greenLight:'#f0fdf4', greenMid:'#dcfce7',
  orange:'#ea580c', orangeLight:'#fff7ed', orangeMid:'#fed7aa',
  red:'#dc2626', redLight:'#fef2f2', redMid:'#fecaca',
  blue:'#2563eb', blueLight:'#eff6ff', blueMid:'#dbeafe',
  slate50:'#f8fafc', slate100:'#f1f5f9', slate200:'#e2e8f0',
  slate400:'#94a3b8', slate500:'#64748b', slate600:'#475569',
  slate800:'#1e293b', slate900:'#0f172a',
  textPrimary:'#0f172a', textSecondary:'#475569', textMuted:'#94a3b8',
  mono:"'JetBrains Mono','Courier New',monospace",
};

const STATUS_META = {
  SAMPLE_COLLECTED: { label:'Collected',   bg:C.orangeMid, color:C.orange, dot:'#ea580c' },
  IN_PROGRESS:      { label:'In Progress', bg:C.blueMid,   color:C.blue,   dot:'#2563eb' },
  COMPLETED:        { label:'Completed',   bg:C.greenMid,  color:C.green,  dot:'#16a34a' },
};

const FLAG_META = {
  NORMAL:   { label:'Normal',      color:C.green,  bg:C.greenMid  },
  HIGH:     { label:'↑ High',      color:C.orange, bg:C.orangeMid },
  LOW:      { label:'↓ Low',       color:C.blue,   bg:C.blueMid   },
  CRITICAL: { label:'!! Critical', color:C.red,    bg:C.redMid    },
};

const injectStyles = () => {
  if (document.getElementById('lab-tech-styles')) return;
  const style = document.createElement('style');
  style.id = 'lab-tech-styles';
  style.textContent = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=JetBrains+Mono:wght@700&display=swap');
    .lt-root * { box-sizing:border-box; font-family:'DM Sans',sans-serif; }
    .lt-input { width:100%; padding:9px 12px; border:1.5px solid #e2e8f0; border-radius:8px;
      font-size:14px; outline:none; color:#0f172a; background:#fff; transition:border-color .15s; }
    .lt-input:focus { border-color:#0284c7; box-shadow:0 0 0 3px rgba(3,105,161,.1); }
    .lt-btn { padding:9px 18px; border:none; border-radius:8px; font-size:13px; font-weight:700;
      cursor:pointer; display:flex; align-items:center; gap:6px; transition:all .15s; }
    .lt-btn:disabled { opacity:.6; cursor:not-allowed; }
    .lt-btn-primary { background:linear-gradient(135deg,#0284c7,#0369a1); color:#fff; }
    .lt-btn-primary:hover:not(:disabled) { filter:brightness(1.08); transform:translateY(-1px); }
    .lt-btn-outline { background:#fff; border:1.5px solid #e2e8f0; color:#475569; }
    .lt-btn-outline:hover { background:#f8fafc; }
    .lt-tr:hover td { background:#f1f7ff !important; }
    .lt-modal-overlay { position:fixed; inset:0; background:rgba(15,23,42,.55);
      display:flex; align-items:center; justify-content:center; z-index:1000;
      backdrop-filter:blur(3px); animation:ltFade .15s ease; }
    .lt-modal { background:#fff; border-radius:16px; box-shadow:0 32px 80px rgba(15,23,42,.25);
      animation:ltSlide .2s ease; }
    .param-row { background:#f8fafc; border-radius:10px; padding:14px 16px;
      margin-bottom:8px; border:1px solid #e2e8f0; }
    @keyframes ltFade  { from{opacity:0} to{opacity:1} }
    @keyframes ltSlide { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
    ::-webkit-scrollbar{width:6px} ::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:99px}
  `;
  document.head.appendChild(style);
};

const Badge = ({ status }) => {
  const m = STATUS_META[status] || { label:status, bg:C.slate100, color:C.slate600, dot:'#94a3b8' };
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:5, background:m.bg, color:m.color,
      padding:'3px 10px', borderRadius:99, fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:m.dot }} />
      {m.label}
    </span>
  );
};

const UrgencyBadge = ({ urgency }) => {
  const map = {
    ROUTINE:{ label:'Routine', color:C.slate500, bg:C.slate100 },
    URGENT: { label:'Urgent',  color:C.orange,   bg:C.orangeLight },
    STAT:   { label:'STAT',    color:C.red,       bg:C.redLight },
  };
  const m = map[urgency] || map.ROUTINE;
  return (
    <span style={{ background:m.bg, color:m.color, padding:'3px 10px',
      borderRadius:6, fontSize:11, fontWeight:800 }}>{urgency}</span>
  );
};

const StatCard = ({ icon, label, value, color }) => (
  <div style={{ background:'#fff', borderRadius:12, padding:18,
    border:'1px solid #e2e8f0', boxShadow:'0 1px 3px rgba(0,0,0,.05)' }}>
    <div style={{ width:38, height:38, borderRadius:9, background:color+'20',
      display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, marginBottom:10 }}>{icon}</div>
    <div style={{ fontSize:24, fontWeight:800, color:C.textPrimary, lineHeight:1, marginBottom:4 }}>{value}</div>
    <div style={{ fontSize:12, fontWeight:600, color:C.textSecondary }}>{label}</div>
  </div>
);

const printBarcodeLabel = (order, roleMeta) => {
  const sample = order.sample;
  if (!sample) return;
  const win = window.open('','_blank','width=400,height=300');
  const bars = sample.barcode.split('').map(c => c.charCodeAt(0));
  const barsHtml = bars.map(b =>
    `<div style="width:${b%3===0?3:b%3===1?2:1}px;height:${b%5===0?48:b%5===1?40:44}px;background:#0f172a;display:inline-block;margin:0 .5px;"></div>`
  ).join('');
  win.document.write(`<html><head><title>Barcode</title>
    <style>body{font-family:sans-serif;text-align:center;padding:20px;background:#f8fafc}
    .label{border:1.5px solid #e2e8f0;padding:20px;display:inline-block;min-width:280px;border-radius:12px;background:#fff}
    .mono{font-family:monospace;font-weight:700;letter-spacing:4px;font-size:14px;margin-top:6px}
    @media print{body{margin:0}}</style></head><body>
    <div class="label">
      <div style="background:${roleMeta.gradient};color:#fff;padding:7px 12px;border-radius:6px;margin-bottom:12px;font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.06em">
        ${roleMeta.icon} ${roleMeta.label} Lab
      </div>
      <div style="font-weight:800;font-size:15px;margin-bottom:3px">${order.patient_name}</div>
      <div style="font-size:11px;color:#64748b;margin-bottom:8px">
        ID: ${order.patient_id} · Token: <strong>${order.token_number}</strong>
      </div>
      <div style="display:flex;justify-content:center;align-items:flex-end;gap:1px;padding:8px;background:#f8fafc;border-radius:6px;border:1px solid #e2e8f0">${barsHtml}</div>
      <div class="mono">${sample.barcode}</div>
      <div style="font-size:11px;color:#475569;margin-top:10px;border-top:1px dashed #e2e8f0;padding-top:8px">
        ${order.items?.filter(i=>!i.category_name||i.category_name===roleMeta.label).map(i=>i.test_name).join(' · ')}
      </div>
    </div>
    <script>window.onload=()=>{window.print();window.close()}<\/script>
    </body></html>`);
  win.document.close();
};

// ─── Result Entry Modal ───────────────────────────────────────────────────────
const ResultEntryModal = ({ order, roleMeta, onClose, onSuccess }) => {
  // Only show items that belong to this tech's lab category.
  // Falls back to showing all items if category_name is missing (older data).
  const relevantItems = (order.items || []).filter(
    i => !i.category_name || i.category_name === roleMeta.label
  );

  const buildInitial = () => {
    const map = {};
    (order.results || []).forEach(r => {
      map[r.parameter] = {
        value_numeric: r.value_numeric || '', value_text: r.value_text || '',
        flag: r.flag || 'NORMAL', notes: r.notes || '',
      };
    });
    return map;
  };

  const [resultsMap,   setResultsMap]   = useState(buildInitial());
  const [markComplete, setMarkComplete] = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState('');

  const updateResult = (paramId, field, value) =>
    setResultsMap(prev => ({ ...prev, [paramId]: { ...prev[paramId], [field]: value } }));

  const autoFlag = (paramId, value, param) => {
    const num = parseFloat(value);
    if (isNaN(num)) { updateResult(paramId, 'flag', 'NORMAL'); return; }
    const min = parseFloat(param.normal_min), max = parseFloat(param.normal_max);
    let flag = 'NORMAL';
    if (!isNaN(min) && !isNaN(max)) {
      if (num < min) flag = num < min * 0.8 ? 'CRITICAL' : 'LOW';
      else if (num > max) flag = num > max * 1.2 ? 'CRITICAL' : 'HIGH';
    }
    updateResult(paramId, 'flag', flag);
  };

  const handleSubmit = async () => {
    setLoading(true); setError('');
    try {
      const allParams = relevantItems.flatMap(item => (item.parameters || []));
      const results = allParams.map(p => ({
        parameter_id:  p.id,
        value_numeric: resultsMap[p.id]?.value_numeric || null,
        value_text:    resultsMap[p.id]?.value_text    || '',
        flag:          resultsMap[p.id]?.flag          || 'NORMAL',
        notes:         resultsMap[p.id]?.notes         || '',
      }));
      const updated = await labAPI.enterResults(order.id, { results, mark_complete: markComplete });
      onSuccess(updated);
    } catch(e) { setError(e.response?.data?.error || 'Failed to save results'); }
    finally { setLoading(false); }
  };

  return (
    <div className="lt-modal-overlay" onClick={onClose}>
      <div className="lt-modal" style={{ width:700, maxHeight:'92vh', display:'flex', flexDirection:'column' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding:'18px 22px', borderBottom:`1px solid ${C.slate200}`,
          background:`linear-gradient(135deg,${roleMeta.bg},#eff6ff)`,
          borderRadius:'16px 16px 0 0', display:'flex', justifyContent:'space-between',
          alignItems:'center', flexShrink:0 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:C.slate900 }}>
              📋 Enter Results — {roleMeta.icon} {roleMeta.label}
            </div>
            <div style={{ fontSize:12, color:C.slate500, marginTop:2 }}>
              {order.patient_name} &nbsp;·&nbsp; Token: <strong>{order.token_number}</strong>
              {order.sample?.barcode && (
                <span style={{ fontFamily:C.mono, fontWeight:700, color:roleMeta.color, marginLeft:10 }}>
                  {order.sample.barcode}
                </span>
              )}
              <span style={{ marginLeft:10, background:roleMeta.bg, color:roleMeta.color,
                border:`1px solid ${roleMeta.border}`, padding:'2px 8px', borderRadius:5,
                fontSize:11, fontWeight:700 }}>
                {relevantItems.length} test{relevantItems.length !== 1 ? 's' : ''} for {roleMeta.label}
              </span>
            </div>
          </div>
          <button onClick={onClose} style={{ background:C.slate100, border:'none',
            width:30, height:30, borderRadius:'50%', cursor:'pointer', fontSize:15, color:C.slate500 }}>✕</button>
        </div>

        <div style={{ padding:'18px 22px', overflowY:'auto', flex:1 }}>
          {error && (
            <div style={{ background:C.redLight, border:`1px solid ${C.redMid}`, color:C.red,
              padding:'9px 12px', borderRadius:8, marginBottom:14, fontSize:13 }}>⚠ {error}</div>
          )}

          {relevantItems.map(item => (
            <div key={item.id || item.test_name} style={{ marginBottom:22 }}>
              <div style={{ fontSize:11, fontWeight:800, letterSpacing:'.07em', textTransform:'uppercase',
                color:roleMeta.color, paddingBottom:8, borderBottom:`2px solid ${roleMeta.border}`,
                marginBottom:12 }}>
                {roleMeta.icon} {item.test_name}
              </div>
              {(!item.parameters || item.parameters.length === 0) && (
                <p style={{ color:C.textMuted, fontSize:13 }}>No parameters defined for this test.</p>
              )}
              {(item.parameters || []).map(param => {
                const r = resultsMap[param.id] || { value_numeric:'', value_text:'', flag:'NORMAL', notes:'' };
                const flagMeta = FLAG_META[r.flag] || FLAG_META.NORMAL;
                const isAbnormal = r.flag !== 'NORMAL';
                return (
                  <div key={param.id} className="param-row"
                    style={isAbnormal ? { borderColor:flagMeta.color+'55', background:flagMeta.bg+'55' } : {}}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <span style={{ fontWeight:700, fontSize:13, color:C.textPrimary }}>{param.name}</span>
                        {param.unit && (
                          <span style={{ color:C.textMuted, fontSize:12, background:C.slate100,
                            padding:'1px 7px', borderRadius:4 }}>{param.unit}</span>
                        )}
                      </div>
                      <div style={{ fontSize:11, color:C.textMuted, fontFamily:C.mono }}>
                        Ref: {param.normal_range_text ||
                          (param.normal_min!=null && param.normal_max!=null
                            ? `${param.normal_min} – ${param.normal_max}` : '—')}
                      </div>
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto', gap:8, alignItems:'center' }}>
                      <input className="lt-input" type="number" placeholder="Numeric value"
                        value={r.value_numeric}
                        onChange={e => { updateResult(param.id,'value_numeric',e.target.value); autoFlag(param.id,e.target.value,param); }} />
                      <input className="lt-input" placeholder="Text result (optional)"
                        value={r.value_text}
                        onChange={e => updateResult(param.id,'value_text',e.target.value)} />
                      <select style={{ padding:'9px 10px', border:`1.5px solid ${C.slate200}`,
                        borderRadius:8, fontSize:13, fontWeight:800, color:flagMeta.color, cursor:'pointer' }}
                        value={r.flag} onChange={e => updateResult(param.id,'flag',e.target.value)}>
                        {Object.entries(FLAG_META).map(([k,v]) => (
                          <option key={k} value={k}>{v.label}</option>
                        ))}
                      </select>
                    </div>
                    {isAbnormal && (
                      <div style={{ marginTop:8 }}>
                        <input className="lt-input" placeholder="Clinical notes for abnormal result…"
                          value={r.notes} onChange={e => updateResult(param.id,'notes',e.target.value)}
                          style={{ fontSize:13, background:'#fff' }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

          <label style={{ display:'flex', alignItems:'center', gap:12, padding:'13px 16px',
            background:C.greenLight, border:`1.5px solid ${C.greenMid}`, borderRadius:10, cursor:'pointer' }}>
            <input type="checkbox" checked={markComplete}
              onChange={e => setMarkComplete(e.target.checked)}
              style={{ width:18, height:18, accentColor:'#0d9488', cursor:'pointer' }} />
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:'#0f766e' }}>
                Mark {roleMeta.label} section as Completed
              </div>
              <div style={{ fontSize:11, color:C.slate500 }}>
                Marks your section's tests as done. The order completes automatically
                when <strong>all categories</strong> have submitted results.
              </div>
            </div>
          </label>
        </div>

        <div style={{ padding:'14px 22px', borderTop:`1px solid ${C.slate200}`,
          display:'flex', gap:10, justifyContent:'flex-end', flexShrink:0 }}>
          <button className="lt-btn lt-btn-outline" onClick={onClose}>Cancel</button>
          <button className="lt-btn lt-btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? '⏳ Saving…' : `💾 Save Results${markComplete ? ' & Complete' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Dashboard ───────────────────────────────────────────────────────────
const thS = { padding:'11px 16px', textAlign:'left', background:C.slate50, color:C.slate500,
  fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:'.06em',
  borderBottom:`2px solid ${C.slate200}` };
const tdS = { padding:'13px 16px', borderBottom:`1px solid ${C.slate200}`,
  color:C.textPrimary, verticalAlign:'middle' };

const LabTechnicianDashboard = () => {
  const { user } = useAuth();
  const roleMeta = ROLE_META[user?.role] || ROLE_META.LAB_TECH_MICROBIOLOGY;

  const [orders,       setOrders]       = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [searchQ,      setSearchQ]      = useState('');
  const [resultModal,  setResultModal]  = useState(null);

  useEffect(() => { injectStyles(); }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      // Analysis stage: SAMPLE_COLLECTED or IN_PROGRESS
      const data = await labAPI.getOrders({ status: 'SAMPLE_COLLECTED,IN_PROGRESS' });
      setOrders(Array.isArray(data) ? data : data.results || []);
    } catch(e) { console.error('Failed to fetch lab orders', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const filtered = orders.filter(o => {
    if (!searchQ) return true;
    const q = searchQ.toLowerCase();
    return (
      o.patient_name?.toLowerCase().includes(q) ||
      o.patient_id?.toLowerCase().includes(q)   ||
      o.token_number?.toLowerCase().includes(q) ||
      o.sample?.barcode?.toLowerCase().includes(q)
    );
  });

  const urgencyOrder = { STAT:0, URGENT:1, ROUTINE:2 };
  const sorted = [...filtered].sort((a,b) => (urgencyOrder[a.urgency]??2)-(urgencyOrder[b.urgency]??2));

  const openResultModal = async (order) => {
    try { setResultModal(await labAPI.getOrder(order.id)); }
    catch { setResultModal(order); }
  };

  return (
    <DashboardLayout title="Lab Analysis">
      <div className="lt-root" style={{ minHeight:'100vh', background:C.pageBg, padding:'24px 28px' }}>

        {/* ── Header ── */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ width:44, height:44, borderRadius:11, background:roleMeta.gradient,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:22, boxShadow:'0 4px 12px rgba(0,0,0,.2)' }}>
              {roleMeta.icon}
            </div>
            <div>
              <h1 style={{ margin:0, fontSize:20, fontWeight:800, color:C.slate900 }}>
                {roleMeta.label} — Analysis & Results
              </h1>
              <p style={{ margin:0, fontSize:12, color:C.textMuted }}>
                Logged in as <strong style={{ color:roleMeta.color }}>{user?.first_name || user?.username}</strong>
                &nbsp;·&nbsp; Showing <strong>{roleMeta.label}</strong> tests only
              </p>
            </div>
          </div>
        </div>

        {/* Info notice about sample collection */}
        <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:10,
          padding:'10px 16px', marginBottom:20, display:'flex', alignItems:'center', gap:10, fontSize:13 }}>
          <span>ℹ️</span>
          <span style={{ color:'#1e40af' }}>
            <strong>Note:</strong> Sample collection is handled by the dedicated
            <strong> Sample Collector</strong> role. This dashboard only shows samples
            already collected and ready for analysis.
          </span>
        </div>

        {/* ── Stat Cards ── */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:14, marginBottom:22 }}>
          <StatCard icon="📋" label="In Queue"   value={orders.length}                               color={roleMeta.color} />
          <StatCard icon="🚨" label="STAT"       value={orders.filter(o=>o.urgency==='STAT').length}  color='#dc2626' />
          <StatCard icon="⚡" label="Urgent"     value={orders.filter(o=>o.urgency==='URGENT').length} color='#ea580c' />
          <StatCard icon="🟢" label="Routine"    value={orders.filter(o=>o.urgency==='ROUTINE').length} color='#16a34a' />
        </div>

        {/* ── Main Card ── */}
        <div style={{ background:'#fff', borderRadius:14, border:`1px solid ${C.slate200}`,
          boxShadow:'0 2px 12px rgba(0,0,0,.06)', overflow:'hidden' }}>

          <div style={{ padding:'16px 22px', borderBottom:`1px solid ${C.slate200}`,
            display:'flex', justifyContent:'space-between', alignItems:'center', background:C.slate50 }}>
            <div>
              <div style={{ fontSize:14, fontWeight:800, color:C.slate900 }}>
                🔬 Analysis & Results
              </div>
              <div style={{ fontSize:12, color:C.textMuted, marginTop:2 }}>
                Collected samples awaiting test results entry
              </div>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <input className="lt-input" style={{ width:280 }}
                placeholder="Search barcode, patient, token…"
                value={searchQ} onChange={e => setSearchQ(e.target.value)} />
              <div style={{ background:roleMeta.bg, color:roleMeta.color, border:`1px solid ${roleMeta.border}`,
                padding:'6px 14px', borderRadius:8, fontSize:13, fontWeight:700 }}>
                {sorted.length} records
              </div>
            </div>
          </div>

          {loading ? (
            <div style={{ padding:56, textAlign:'center', color:C.textMuted }}>
              <div style={{ fontSize:32, marginBottom:12 }}>⏳</div>
              <div style={{ fontWeight:600 }}>Loading orders…</div>
            </div>
          ) : sorted.length === 0 ? (
            <div style={{ padding:56, textAlign:'center', color:C.textMuted }}>
              <div style={{ fontSize:44, marginBottom:12 }}>{roleMeta.icon}</div>
              <div style={{ fontWeight:700, fontSize:15, color:C.slate600, marginBottom:4 }}>
                {searchQ ? 'No results found' : `No ${roleMeta.label} samples awaiting analysis`}
              </div>
              <div style={{ fontSize:13 }}>
                {searchQ ? 'Try a different search term' : 'Samples appear here once collected by the Sample Collector'}
              </div>
            </div>
          ) : (
            <div style={{ overflowX:'auto' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                <thead>
                  <tr>
                    {['Patient', 'Token', 'Tests', 'Urgency', 'Barcode', 'Status', 'Actions'].map(h => (
                      <th key={h} style={thS}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(order => (
                    <tr key={order.id} className="lt-tr">
                      <td style={tdS}>
                        <div style={{ fontWeight:700, fontSize:14 }}>{order.patient_name}</div>
                        <div style={{ fontSize:11, color:C.textMuted, marginTop:2 }}>{order.patient_id}</div>
                      </td>
                      <td style={tdS}>
                        <span style={{ fontFamily:C.mono, fontWeight:700, fontSize:13, color:roleMeta.color }}>
                          {order.token_number || '—'}
                        </span>
                      </td>
                      <td style={{ ...tdS, maxWidth:220 }}>
                        <div style={{ fontSize:12, color:C.slate600, lineHeight:1.5 }}>
                          {order.items
                            ?.filter(i => !i.category_name || i.category_name === roleMeta.label)
                            .map(i=>i.test_name).join(' · ')}
                        </div>
                      </td>
                      <td style={tdS}><UrgencyBadge urgency={order.urgency} /></td>
                      <td style={tdS}>
                        {order.sample ? (
                          <span style={{ fontFamily:C.mono, fontSize:12, fontWeight:700,
                            color:C.slate700, background:C.slate100, padding:'3px 8px', borderRadius:5 }}>
                            {order.sample.barcode}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={tdS}><Badge status={order.status} /></td>
                      <td style={{ ...tdS, whiteSpace:'nowrap' }}>
                        <div style={{ display:'flex', gap:6 }}>
                          {order.sample && (
                            <button className="lt-btn lt-btn-outline"
                              style={{ padding:'6px 12px', fontSize:12 }}
                              onClick={() => printBarcodeLabel(order, roleMeta)}>
                              🖨 Print
                            </button>
                          )}
                          <button className="lt-btn lt-btn-primary"
                            style={{ padding:'6px 12px', fontSize:12 }}
                            onClick={() => openResultModal(order)}>
                            📋 Enter Results
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {resultModal && (
        <ResultEntryModal
          order={resultModal} roleMeta={roleMeta}
          onClose={() => setResultModal(null)}
          onSuccess={() => { setResultModal(null); fetchOrders(); }}
        />
      )}
    </DashboardLayout>
  );
};

export default LabTechnicianDashboard;